export const SEGMENT_LENGTH_FT = 528;

export const PAY_RANGES = [
  { code: 'I', min: 0, max: 37, payFactor: 1.06, deductionRate: 0 },
  { code: 'J', min: 37, max: 43, payFactor: 1.05, deductionRate: 0 },
  { code: 'K', min: 43, max: 49, payFactor: 1.03, deductionRate: 0 },
  { code: 'L', min: 49, max: 56, payFactor: 1.02, deductionRate: 0 },
  { code: 'M', min: 56, max: 68, payFactor: 1.00, deductionRate: 0 },
  { code: 'N', min: 68, max: 74, payFactor: 0.98, deductionRate: 0 },
  { code: 'O', min: 74, max: 80, payFactor: 0.96, deductionRate: 0 },
  { code: 'P', min: 80, max: 86, payFactor: 0.94, deductionRate: 0 },
  { code: 'Q', min: 86, max: 93, payFactor: 0.92, deductionRate: 0 },
  { code: 'R', min: 93, max: 96, payFactor: 0.90, deductionRate: 0 },
  { code: 'S', min: 96, max: 105, payFactor: 0.90, deductionRate: 1000 },
  { code: 'T', min: 105, max: Infinity, payFactor: 0.90, deductionRate: 2000 },
];

export const DEFAULT_SETTINGS = {
  excluded: false,
};

const LENGTH_MISMATCH_TOLERANCE_FT = 1;

const toFiniteNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const fmtNebraskaRange = (row) => {
  if (row.max === Infinity) return `${row.min}+`;
  return `${row.min}-${row.max}`;
};

export const getNebraskaRange = (avgIri) => {
  for (const row of PAY_RANGES) {
    if (avgIri < row.max) return row;
  }
  return PAY_RANGES[PAY_RANGES.length - 1];
};

export const getNebraskaSegmentLength = (seg) => {
  const explicitLength = toFiniteNumber(seg.lengthFt ?? seg.length_ft ?? seg.distance_ft ?? seg.length);
  if (explicitLength > 0) return explicitLength;

  const startStation = toFiniteNumber(seg.from_station_ft ?? seg.startStationFt, 0);
  const stopStation = toFiniteNumber(seg.to_station_ft ?? seg.stopStationFt, 0);
  return Math.abs(stopStation - startStation);
};

export const getNebraskaWheelPathIris = (seg) => {
  const rightIri = toFiniteNumber(seg.iri_right_in_per_mi ?? seg.iriRightWheelPath ?? seg.right_iri);
  const leftIri = toFiniteNumber(seg.iri_left_in_per_mi ?? seg.iriLeftWheelPath ?? seg.left_iri);
  return { leftIri, rightIri };
};

export const getNebraskaSegmentIri = (seg) => {
  const { leftIri, rightIri } = getNebraskaWheelPathIris(seg);
  if (Number.isFinite(rightIri) && Number.isFinite(leftIri)) {
    return (rightIri + leftIri) / 2;
  }

  return toFiniteNumber(seg.iri_avg_in_per_mi ?? seg.iri_avg ?? seg.iri_in_per_mi, 0);
};

export const calculateNebraskaPayFactor = (segments, settings = DEFAULT_SETTINGS, isReversed = false) => {
  if (!segments?.length) return null;

  const validSegments = segments
    .filter(seg => !seg.exception)
    .map((seg) => {
      const startStation = toFiniteNumber(seg.from_station_ft ?? seg.startStationFt, 0);
      const stopStation = toFiniteNumber(seg.to_station_ft ?? seg.stopStationFt, 0);
      return {
        ...seg,
        startStation,
        stopStation,
      };
    });

  if (validSegments.length === 0) return null;

  const sortedSegments = [...validSegments].sort((a, b) =>
    isReversed
      ? b.startStation - a.startStation
      : a.startStation - b.startStation
  );

  const rangeSummary = PAY_RANGES.map(row => ({
    ...row,
    totalLengthFt: 0,
    weightedLength: 0,
    deductionDollars: 0,
    segmentCount: 0,
  }));

  let totalWeightedLength = 0;
  let totalLengthFt = 0;
  let totalDeductionDollars = 0;
  let iriLengthSum = 0;
  let lengthMismatchCount = 0;

  const rows = sortedSegments.map((seg, index) => {
    const { leftIri, rightIri } = getNebraskaWheelPathIris(seg);
    const avgIri = getNebraskaSegmentIri(seg);
    const lengthFt = getNebraskaSegmentLength(seg);
    const stationLength = Math.abs(seg.stopStation - seg.startStation);
    const hasLengthMismatch = stationLength > 0 && Math.abs(lengthFt - stationLength) > LENGTH_MISMATCH_TOLERANCE_FT;
    const range = getNebraskaRange(avgIri);
    const deductionDollars = settings.excluded ? 0 : range.deductionRate * (lengthFt / SEGMENT_LENGTH_FT);
    const weightedLength = range.payFactor * lengthFt;
    const summary = rangeSummary.find(item => item.code === range.code);

    if (summary) {
      summary.totalLengthFt += lengthFt;
      summary.weightedLength += weightedLength;
      summary.deductionDollars += deductionDollars;
      summary.segmentCount += 1;
    }

    totalWeightedLength += weightedLength;
    totalLengthFt += lengthFt;
    totalDeductionDollars += deductionDollars;
    iriLengthSum += avgIri * lengthFt;
    if (hasLengthMismatch) lengthMismatchCount += 1;

    const displayStart = isReversed ? seg.stopStation : seg.startStation;
    const displayStop = isReversed ? seg.startStation : seg.stopStation;

    return {
      num: index + 1,
      segmentId: seg.segment_id,
      sessionId: seg.session_id,
      sessionInfo: seg.sessionInfo,
      startStation: displayStart,
      stopStation: displayStop,
      lengthFt,
      leftIri,
      rightIri,
      avgIri,
      rangeLabel: fmtNebraskaRange(range),
      payFactor: range.payFactor,
      deductionDollars,
      weightedLength,
      hasLengthMismatch,
      hasIssue: range.deductionRate > 0,
    };
  });

  const overallPayFactor = totalLengthFt > 0 ? totalWeightedLength / totalLengthFt : 0;
  const avgIri = totalLengthFt > 0 ? iriLengthSum / totalLengthFt : 0;

  return {
    rows,
    rangeSummary,
    overallPayFactor,
    avgIri,
    totalLengthFt,
    totalWeightedLength,
    totalDeductionDollars,
    segmentCount: rows.length,
    deductionSegmentCount: rows.filter(row => row.hasIssue).length,
    lengthMismatchCount,
  };
};
